import { useCallback, useEffect, useState } from "react";
import {
  addCartItem,
  clearCart,
  fetchCart,
  removeCartItem,
  updateCartItemQty,
} from "../api/client.js";
import type { CartResponseDTO } from "../api/types.js";

export interface CartState {
  loading: boolean;
  mutating: boolean;
  error: string | null;
  cart: CartResponseDTO | null;
  itemCount: number;
  reload: () => void;
  addItem: (productId: string, qty?: number) => Promise<void>;
  setQty: (productId: string, qty: number) => Promise<void>;
  removeItem: (productId: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

export function useCart(options: { autoLoad?: boolean } = {}): CartState {
  const { autoLoad = true } = options;
  const [loading, setLoading] = useState(autoLoad);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartResponseDTO | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchCart();
        if (!cancelled) {
          setCart(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Cart failed");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [autoLoad, reloadToken]);

  const runMutation = useCallback(async (action: () => Promise<CartResponseDTO>) => {
    setMutating(true);
    setError(null);
    try {
      const data = await action();
      setCart(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cart update failed");
      throw err;
    } finally {
      setMutating(false);
    }
  }, []);

  const addItem = useCallback(
    async (productId: string, qty = 1) => {
      await runMutation(() => addCartItem(productId, qty));
    },
    [runMutation],
  );

  const setQty = useCallback(
    async (productId: string, qty: number) => {
      await runMutation(() => updateCartItemQty(productId, qty));
    },
    [runMutation],
  );

  const removeItem = useCallback(
    async (productId: string) => {
      await runMutation(() => removeCartItem(productId));
    },
    [runMutation],
  );

  const clearAll = useCallback(async () => {
    await runMutation(() => clearCart());
  }, [runMutation]);

  return {
    loading,
    mutating,
    error,
    cart,
    itemCount: cart?.item_count ?? 0,
    reload,
    addItem,
    setQty,
    removeItem,
    clearAll,
  };
}
